import * as React from 'react';
import * as moment from 'moment';
import {Pdf} from "../../pdf/Pdf";
import {
    FoundationsReportBottomContent,
    FoundationsReportLeftAndRightPair, FoundationsReportTeamContent,
    FoundationsReportTopContent,
    FoundationsReportDichotomiesContent,
    getNormalizedCompares,
    PVAStyle
} from '../../reports';
import {BaseRouteProps} from '../../../interfaces';
import {SurveyVersion, Style, StyleText, CharacteristicGroup, Characteristic, SurveyReportType} from '../../../models';
import {ReportType} from "../../../enums";
import {FilterStore} from "../../../stores";
import { NotificationService, CharacteristicGroupDataService, SurveyReportTypeDataService } from '../../../services';
import './pdf_foundations.css';


interface PdfFoundationsProps extends Partial<BaseRouteProps<{}>> {
    selectedUserData: any;
    surveyVersion: SurveyVersion;
    allStyles: Style[];
    notificationService: NotificationService;
    FilterStore?: FilterStore;
    characteristicGroups?: { [id: number]: CharacteristicGroup };
    characteristics?: { [id: number]: Characteristic };
    surveyReportTypes?: SurveyReportType[];
}

interface PdfFoundationsState {
    characteristicGroups: { [id: number]: CharacteristicGroup };
    characteristics: { [id: number]: Characteristic };
    surveyReportTypes: SurveyReportType[];
}

const getContent = (currentStyle: Style, type: string) => {
    if (currentStyle && currentStyle.styleTexts) {
        const result = currentStyle.styleTexts.find((st: StyleText) => {
            return st.styleTextType === type;
        });
        if (result) {
            return result.value;
        }
    }
    return '';
};

export class PdfFoundations extends React.Component<PdfFoundationsProps, PdfFoundationsState> {
    private characteristicGroupDataService = new CharacteristicGroupDataService(this.props.notificationService);
    private surveyReportTypeDataService = new SurveyReportTypeDataService(this.props.notificationService);

    constructor(props: PdfFoundationsProps) {
        super(props);
        this.state = {
            characteristicGroups: {},
            characteristics: {},
            surveyReportTypes: []
        };
    }

    componentDidMount() {
        const { selectedUserData, surveyVersion } = this.props;
        if (surveyVersion) {
            this.surveyReportTypeDataService.getBySurveyVersion(surveyVersion.id).then((surveyReportTypes: SurveyReportType[]) => {
                this.setState({ surveyReportTypes: surveyReportTypes });
            });
        }
        if (selectedUserData && selectedUserData.inclinationGroups) {
            selectedUserData.inclinationGroups.forEach((inclinationGroup: any) => {
                const id = inclinationGroup.characteristicGroupID;
                if (id && !this.state.characteristicGroups[id]) {
                    this.characteristicGroupDataService.get(id).then((characteristicGroup: CharacteristicGroup) => {
                        this.setState((prevState) => {
                            const characteristics = { ...prevState.characteristics };
                            if (characteristicGroup.characteristicGroupCharacteristics && characteristicGroup.characteristicGroupCharacteristics.length > 0) {
                                characteristicGroup.characteristicGroupCharacteristics.forEach(c => {
                                    if (c.characteristic) {
                                        characteristics[c.characteristic.id] = c.characteristic;
                                    }
                                });
                            }
                            return {
                                characteristicGroups: {
                                    ...prevState.characteristicGroups,
                                    [id]: characteristicGroup
                                },
                                characteristics: characteristics
                            };
                        });
                    });
                }
            });
        }
    }

    get surveyReportType() {
        if (this.props.FilterStore && this.props.FilterStore.surveyReportType) {
            return this.props.FilterStore.surveyReportType;
        }
        return ReportType.Standard;
    }
    
    private getCurrentStyle() {
        const { selectedUserData, allStyles } = this.props;
        if (selectedUserData && selectedUserData.style) {
            let userStyle = selectedUserData.style;
            if (allStyles && allStyles.length > 0) {
                const foundStyle = allStyles.find((style: Style) => {
                    return style.name === selectedUserData.style.name;
                });
                if (foundStyle) {
                    userStyle = foundStyle;
                }
            }
            return userStyle;
        }
        return null;
    }

    public render() {
        const { selectedUserData, surveyVersion, allStyles, notificationService } = this.props;
        
        const currentStyle = this.getCurrentStyle();
        
        if (!selectedUserData || !currentStyle) {
            return null;
        }

        const badgeColor = currentStyle ? `var(--pva-${currentStyle.name.toLowerCase()})` : 'var(--blue)';
        const currentStyleName = currentStyle ? currentStyle.name.toLowerCase() : '';
        const checkmarkUrl = currentStyleName ? `url("/images/checkmark-${currentStyleName}.svg")` : 'url("/images/checkmark.svg")';
        const checkMarkRedUrl = currentStyleName ? `url("/images/check-mark-${currentStyleName}.svg")` : 'url("/images/check mark red.svg")';

        const characteristicGroups = (Object.keys(this.state.characteristicGroups).length > 0) ? this.state.characteristicGroups : (this.props.characteristicGroups || {});
        const characteristics = (Object.keys(this.state.characteristics).length > 0) ? this.state.characteristics : (this.props.characteristics || {});
        const surveyReportTypes = (this.state.surveyReportTypes.length > 0) ? this.state.surveyReportTypes : (this.props.surveyReportTypes && this.props.surveyReportTypes.length > 0 ? this.props.surveyReportTypes : (this.props.FilterStore && this.props.FilterStore.surveyReportTypes ? this.props.FilterStore.surveyReportTypes : []));

        const normalizedCompares = getNormalizedCompares(selectedUserData, characteristicGroups, characteristics, surveyReportTypes);

        // Get additional style texts (strengths, communication, ideal environment, etc.)
        const styleTexts = currentStyle && currentStyle.styleTexts || [];
        const sortedAdditionalStyles: StyleText[] = styleTexts.filter((styleText: StyleText) => {
            return styleText.styleTextType === 'additional';
        }).sort((one: StyleText, two: StyleText) => {
            if (one.sort > two.sort) {
                return 1;
            } else if (one.sort < two.sort) {
                return -1;
            }
            return 0;
        });

        let page = 1;
        const maxPage = 3 + sortedAdditionalStyles.length + (normalizedCompares.length > 0 ? 1 : 0);
        const organizationName = this.props.FilterStore && this.props.FilterStore.organization ? this.props.FilterStore.organization.name : '';

        return (
            <Pdf fontSize="8.5px">
                <Pdf.TitlePage
                    title="Pondera® Virtual Advisor"
                    subtitle="PVA Style Report"
                    name={selectedUserData.name}
                    date={moment().format('MMMM D, YYYY')}
                    organization={organizationName}
                    currentPage={page++}
                    totalPages={maxPage}
                    preventInteraction={true}
                />

                <Pdf.Page>
                    <PVAStyle
                        notificationService={notificationService}
                        history={this.props.history as any}
                        location={this.props.location as any}
                        match={this.props.match as any}
                        style={currentStyle}
                        brief={getContent(currentStyle, 'brief')}
                        content={getContent(currentStyle, 'content')}
                        name={selectedUserData.name}
                        surveyVersion={surveyVersion}
                        allStyles={allStyles}
                        selectedUserData={selectedUserData}
                        hideDichotomies={true}
                        hideButtons={true}
                        showTraits={true}
                    />

                    <div style={{ '--custom-content-color': badgeColor, '--checkmark-url': checkmarkUrl, '--check-mark-url': checkMarkRedUrl } as any}>
                        <FoundationsReportTopContent style={currentStyle} />
                        <FoundationsReportLeftAndRightPair style={currentStyle} badgeColor={badgeColor} />
                        <FoundationsReportBottomContent style={currentStyle} />
                    </div>

                    <Pdf.Footer currentPage={page++} totalPages={maxPage} />
                </Pdf.Page>

                <Pdf.Page>
                    <div style={{ '--custom-content-color': badgeColor, '--checkmark-url': checkmarkUrl, '--check-mark-url': checkMarkRedUrl } as any}>
                        <FoundationsReportTeamContent style={currentStyle as any} />
                    </div>

                    <Pdf.Footer currentPage={page++} totalPages={maxPage} />
                </Pdf.Page>

                {normalizedCompares.length > 0 && (
                    <Pdf.Page>
                        <div className="custom-content">
                            <div className="font-second-level title" style={{ color: badgeColor, marginBottom: '20px' }}>Key Dichotomies</div>
                            <FoundationsReportDichotomiesContent normalizedCompares={normalizedCompares} badgeColor={badgeColor} />
                        </div>

                        <Pdf.Footer currentPage={page++} totalPages={maxPage} />
                    </Pdf.Page>
                )}

                {sortedAdditionalStyles.map((styleText: StyleText) => {
                    return <Pdf.Page key={styleText.keyName}>
                        <PVAStyle
                            notificationService={notificationService}
                            history={this.props.history as any}
                            location={this.props.location as any}
                            match={this.props.match as any}
                            style={currentStyle}
                            brief={getContent(currentStyle, 'brief')}
                            content={getContent(currentStyle, 'content')}
                            name={selectedUserData.name}
                            surveyVersion={surveyVersion}
                            allStyles={allStyles}
                            selectedUserData={selectedUserData}
                            hideDichotomies={true}
                            hideButtons={true}
                            showTraits={true}
                        />

                        {styleText.keyName && <div className={`custom-content ${styleText.keyName.toLowerCase().trim().replace(" ", "-")}-content`} style={{ '--custom-content-color': badgeColor, '--checkmark-url': checkmarkUrl, '--check-mark-url': checkMarkRedUrl } as any} key={styleText.value} dangerouslySetInnerHTML={{ __html: styleText.value}}/>}

                        <Pdf.Footer currentPage={page++} totalPages={maxPage} />
                    </Pdf.Page>;
                })}
            </Pdf>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/reports/individual/pdf_foundations.tsx